import React from 'react';
import { observer } from 'mobx-react';
import { Grid, GridItem } from '@patternfly/react-core';
import * as Sentry from '@sentry/react';

import {
  AlertGroupProvider,
  FeatureProvider,
  LabsAlertGroupProvider,
} from '@ole-ui/ole-ui-components';
import useMobxStores from 'hooks/useMobxStores';
import { EnablementFeatureFlags } from 'config/featureFlags';
import LabUsageAlert from 'components/LabEnvironment/LabUsageAlert';
import Header from '../Header';
import Main from '../Main';
import Footer from '../Footer';
import Intercom from '../Intercom';
import SearchAutosuggest from '../Search';
import ImpersonatingUserBanner from '../Banners/ImpersonatingUserBanner';
import Drawer from '../Drawer';
import SessionModal from '../SessionModal';
import PendoWidget from '../PendoWidget';
import LabPassedAlerts from '../Alert/LabPassedAlerts';
import LabsAlerts from '../Alert/LabsAlerts';
import ServiceWorkerUpdateAlert from '../Alert/ServiceWorkerUpdateAlert';

const EXCLUDED_ROUTES = [
  'skills-paths',
  'catalog',
  'catalog.early-access',
  'courses:page',
  'courses:view',
  'courses:overview',
  'premium:sessions:overview',
  'premium:sessions:calendar',
  'premium:sessions:live',
  'premium:sessions:schedule',
  'free-trial-registration',
  'custom-skill-path',
  'custom-skill-path-edit',
  'home',
];

const Layout = observer(() => {
  const {
    configStore,
    eventStore,
    featuresStore,
    routerStore,
    sessionStore,
    userStore,
  } = useMobxStores();

  const { config } = configStore;
  const { route } = routerStore;
  const isNewSearchEnabled =
    featuresStore.findIsFeatureEnabledBySectionFlagName(
      'enablements',
      EnablementFeatureFlags.NewSearch,
      false,
    );

  return (
    <FeatureProvider
      handleFeatureCheck={featuresStore.isAFeatureFlagEnabled}
      features={featuresStore.features}
    >
      <AlertGroupProvider>
        <LabsAlertGroupProvider alertGroupClassName="labs-alert-group">
          {route && <Header />}

          {userStore.isImpersonatingUser && (
            <ImpersonatingUserBanner
              {...userStore.impersonatedUser}
              subscriptionType={userStore.currentSubscriptionCode}
              clearAction={() => userStore.clearImpersonatedUser()}
            />
          )}

          {userStore.isLoggedIn &&
            !userStore.isOpenSubscriber &&
            route &&
            !EXCLUDED_ROUTES.includes(route?.name) &&
            route?.name === 'search' &&
            featuresStore.allFeaturesFetched &&
            !isNewSearchEnabled && (
              <div className="container-grid-responsive">
                <Grid>
                  <GridItem span={12}>
                    <SearchAutosuggest />
                  </GridItem>
                </Grid>
              </div>
            )}

          {userStore.isLoggedIn && userStore.canShowLabUsageAlert ? (
            <LabUsageAlert />
          ) : null}

          {userStore.isLoggedIn && sessionStore.sessionExpireTime && (
            <SessionModal expirationTime={sessionStore.sessionExpireTime} />
          )}

          <Main />

          {userStore.isLoggedIn && (
            <>
              <LabsAlerts />

              <LabPassedAlerts />
            </>
          )}

          {config?.INTERCOM_IO_ENABLED && config?.INTERCOM_IO_APP_ID ? (
            <Intercom appID={config?.INTERCOM_IO_APP_ID} />
          ) : null}

          {userStore.isLoggedIn &&
          configStore?.pendoAppId &&
          userStore.privacyConsents.functional ? (
            <PendoWidget />
          ) : null}

          <Footer />

          {eventStore.isDrawerOpen && <Drawer />}

          <ServiceWorkerUpdateAlert />
        </LabsAlertGroupProvider>
      </AlertGroupProvider>
    </FeatureProvider>
  );
});

export default Sentry.withProfiler(Layout);
